import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  LdsBadge,
  LdsButton,
   LdsIcon,
  LdsLink,
   LdsModal,
  LdsToast,
  useLdsModal,
  useToastContext,
} from "@elilillyco/ux-lds-react";

import DeleteModal from "./DeleteModal";

import { UPDATE_LIST_STATUS, FETCH_OVERVIEW, UNDO_ARCHIVE_LIST } from "../../store/overview";
import { FETCH_USER_LIST } from "../../store/admin";
import sendReminder from "../../api/overview/sendReminder";
import getSpecialityList from "../../api/details/getSpecialityList";

import "./../../assets/scss/overview/overviewTile.scss";
import { Fragment, useEffect, useState } from "react";
import ArchiveConfirmationModal from "./ArchiveConfirmationModal";
import UndoArchiveConfirmationModal from "./UndoArchiveConfirmationModal";
import infoToastConfig from "../shared/Toast";
import StepTracker from "../shared/StepTracker";

const OverviewTile = ({
  listRecords,
  isModalOpen,
  setIsModalOpen,
  showSuccessToast,
  filters,
  isCurrent,
  resetFilters,
  selectedUserForFilter,
}) => {
  const userId = useSelector(({ user }) => user.uuid);
  const userName = useSelector(({ user }) => user.name);
  const userEmail = useSelector(({ user }) => user.emailId);
  const { selectedBrand } = useSelector(({ overview }) => overview);
  const { userTableList } = useSelector(({ admin }) => admin);

  const { addToast } = useToastContext();

  const dispatch = useDispatch();

  const [visibleListRecords, setVisibleListRecords] = useState([]);
  const [archiveListID, setArchiveListID] = useState("");
  const [undoArchiveListID, setUndoArchiveListID] = useState("");
  const [selectedListItem, setSelectedListItem] = useState(null);
  const [fullSelectedListItem, setFullSelectedListItem] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deptFilter, setDeptFilter] = useState("ALL"); // "ALL", "COMMERCIAL", "MEDICAL"

  // Cooldown state for reminder button
  const [remindCooldowns, setRemindCooldowns] = useState({});

  useEffect(() => {
    dispatch(FETCH_USER_LIST());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    isModalOpen: isArchiveConfirmationModalOpen,
    setIsModalOpen: setIsArchiveConfirmationModalOpen,
  } = useLdsModal();

  const {
    isModalOpen: isUndoArchiveModalOpen,
    setIsModalOpen: setIsUndoArchiveModalOpen,
  } = useLdsModal();

  const {
    isModalOpen: isInfoModalOpen,
    setIsModalOpen: setIsInfoModalOpen,
  } = useLdsModal();

  /**
   * Check if cooldown is active for a given status
   */
  const isCooldownActive = (status) => {
    const cooldownInfo = remindCooldowns[status];
    if (!cooldownInfo || !cooldownInfo.cooldown_until) return false;
    
    const now = new Date();
    const cooldownUntil = new Date(cooldownInfo.cooldown_until);
    return now < cooldownUntil;
  };

  /**
   * Handle the Remind button click from StepTracker.
   * Sends a reminder email for the selected list at its current status.
   */
  const handleRemind = async (stepStatus) => {
    if (!selectedListItem) return;
    setIsInfoModalOpen(false);
    try {
      const response = await sendReminder({
        uuid: selectedListItem.uuid,
        current_status: stepStatus,
      });

      // Update cooldown state if backend returns cooldown info
      if (response.data?.data?.cooldown_until) {
        setRemindCooldowns(prev => ({
          ...prev,
          [stepStatus]: {
            cooldown_until: response.data.data.cooldown_until,
            hours_remaining: response.data.data.hours_remaining
          }
        }));
      }

      addToast(
        infoToastConfig("Reminder email sent successfully!", "success")
      );
    } catch (err) {
      // Handle cooldown error (429 status)
      if (err?.response?.status === 429 && err?.response?.data?.data?.cooldown_until) {
        setRemindCooldowns(prev => ({
          ...prev,
          [stepStatus]: {
            cooldown_until: err.response.data.data.cooldown_until,
            hours_remaining: err.response.data.data.hours_remaining
          }
        }));
      }

      addToast(
        infoToastConfig(
          err?.response?.data?.message || "Failed to send reminder email.",
          "error"
        )
      );
    }
  };


  /**
   * Change the date format to MMMM DD, YYYY
   *
   * @param {Date} dateObj
   * @returns string of formatted date
   */
  const convertDateFormat = (dateObj) =>
    dateObj ? moment(dateObj).format("MMMM DD, YYYY") : "";

  /**
   * Get the expiration status class based on how close the expiration date is
   *
   * @param {string} expirationDate
   * @returns {string} CSS class name for styling
   */
  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return "";
    const now = moment();
    const expDate = moment(expirationDate);
    if (expDate.isBefore(now)) return "expired";
    if (expDate.diff(now, "days") <= 30) return "expiring-soon";
    return "valid";
  };

  /**
   * Get the user full name
   *
   * @param {Object} details
   * @returns string of user name
   */
  const getCreatedBy = (details) =>
    `${details?.first_name} ${details?.last_name}`.trim();

  /**
   * Checks if user can create lists (and therefore delete drafts)
   * Only roles that can create should be able to delete drafts
   * Allowed: SA, AD, HCP, HCPSF, OMN
   * Blocked: VIEW, MLR-R, MLR-A, INC, BIA
   * 
   * @returns true if user can delete draft lists
   */
  const canDeleteDrafts = () => {
    const role = sessionStorage.getItem("role");
    const allowedRoles = [
      "spclty_hub_super_admin",
      "spclty_hub_admin",
      "spclty_hub_hcp_marketer",
      "spclty_hub_hcp_sf_marketer",
      "spclty_hub_omnichannel_advisor"
    ];
    return allowedRoles.includes(role);
  };

  /**
   * Checks if user is a super admin
   *
   * @returns true if user is super admin else false
   */
  const isSuperAdminRole = () =>
    sessionStorage.getItem("role") === "spclty_hub_super_admin";
  
  /**
   * Checks if user can archive lists (Super Admin or Admin)
   *
   * @returns true if user is super admin or admin else false
   */
  const canArchiveLists = () => {
    const role = sessionStorage.getItem("role");
    return role === "spclty_hub_super_admin" || role === "spclty_hub_admin";
  };

  /**
   * Delete the list and show the modal
   *
   * @param {String} itemID
   */
  const deleteList = async (itemID) => {
    const { meta } = await dispatch(
      UPDATE_LIST_STATUS({
        updated_by: userId,
        active: false,
        deleteDraftID: itemID,
      })
    );
    if (meta.requestStatus === "fulfilled") {
      setIsModalOpen(true);
    }
  };

  const dateToUse = (record) => {
    return (record.list_status || "").toUpperCase() === "APPROVED"
      ? record.updated_date
      : record.created_date;
  };

  const sortDate = (array) => {
    const copy = JSON.parse(JSON.stringify(array));
    copy.sort((a, b) => {
      const aDate = dateToUse(a);
      const bDate = dateToUse(b);

      if (aDate < bDate) {
        return 1;
      }
      if (aDate > bDate) {
        return -1;
      }
      return 0;
    });
    return copy;
  };

  const sortRecords = () => {
    let commercialRecords = [];
    let medicalRecords = [];
    visibleListRecords
      .filter((record) => record.show)
      .forEach((record) => {
        if (record.department_field && record.department_field.toUpperCase() === "RETAIL") {
          commercialRecords.push(record);
        } else {
          medicalRecords.push(record);
        }
      });
    commercialRecords = sortDate(commercialRecords);
    medicalRecords = sortDate(medicalRecords);

    return [commercialRecords, medicalRecords];
  };

  const archiveList = async () => {
    const { meta } = await dispatch(
      UPDATE_LIST_STATUS({
        updated_by: userId,
        list_status: "ARCHIVE",
        uuid: archiveListID,
      })
    );

    if (meta.requestStatus === "fulfilled") {
      dispatch(
        FETCH_OVERVIEW({
          brand: selectedBrand,
          created_by: userId,
          user_role: sessionStorage.getItem("role"),
        })
      );
    } else {
      addToast(infoToastConfig("Failed to archive the list.", "error"));
    }
  };

  /**
   * Undo an automatic expiration-archive on a list (Super Admin only).
   * Restores list_status=APPROVED + active=true with optional new expiration date.
   */
  const undoArchive = async (newExpirationDate) => {
    const { meta } = await dispatch(
      UNDO_ARCHIVE_LIST({
        uuid: undoArchiveListID,
        updated_by: userId,
        expiration_date: newExpirationDate || undefined,
      })
    );

    if (meta.requestStatus === "fulfilled") {
      setIsUndoArchiveModalOpen(false);
      addToast(
        infoToastConfig("List restored successfully.", "success")
      );
      dispatch(
        FETCH_OVERVIEW({
          brand: selectedBrand,
          created_by: userId,
          user_role: sessionStorage.getItem("role"),
        })
      );
    } else {
      addToast(
        infoToastConfig(
          "Failed to restore the list. The list may no longer be in an expired-archive state.",
          "error"
        )
      );
    }
  };

  /**
   * A list is eligible for Undo Archive only if it was automatically archived
   * due to expiration — i.e. list_status=ARCHIVE AND active=false.
   * (Manual archives leave active=true.)
   */
  const isExpirationArchived = (item) =>
    (item.list_status || "").toUpperCase() === "ARCHIVE" &&
    item.active === false;

  useEffect(() => {
    if (archiveListID) {
      setIsArchiveConfirmationModalOpen(true);
    }
  }, [archiveListID, setIsArchiveConfirmationModalOpen]);

  useEffect(() => {
    if (!isArchiveConfirmationModalOpen) {
      setArchiveListID("");
    }
  }, [isArchiveConfirmationModalOpen]);

  useEffect(() => {
    if (undoArchiveListID) {
      setIsUndoArchiveModalOpen(true);
    }
  }, [undoArchiveListID, setIsUndoArchiveModalOpen]);

  useEffect(() => {
    if (!isUndoArchiveModalOpen) {
      setUndoArchiveListID("");
    }
  }, [isUndoArchiveModalOpen]);

  const renderList = (list) =>
    list.map((item, index) => {
      const status = (item.list_status || "").toUpperCase();
      const isExpired =
        status === "EXPIRED" ||
        getExpirationStatus(item.expiration_date) === "expired";
      const showExpiredBadge = !isCurrent && isExpired;
      
      return (
        <div key={`listItem_${index}`} className="list-blk">
          <hr className="mt-3 lds-hr" />
          <div className="list-content">
            <div className="created-txt">
              {convertDateFormat(
                status === "APPROVED"
                  ? item.updated_date
                  : item.created_date
              )}{" "}
              | Created by {getCreatedBy(item.created_by)}
              {isCurrent && item.list_status && status !== "ARCHIVE" ? (
                <LdsBadge
                  className={`ml-4 ${status === "DRAFT" ? "draft-badge" : "green-btn"}`}
                >
                  {status}
                </LdsBadge>
              ) : null}
              {status === "ARCHIVE" && !showExpiredBadge && (
                <LdsBadge className="ml-4 green-btn">ARCHIVE</LdsBadge>
              )}
              {showExpiredBadge && (
                <LdsBadge className="ml-4 expiration-tag expired">EXPIRED</LdsBadge>
              )}
              {item.list_quarter !== "" ? (
                <LdsBadge className="ml-4 light-green-btn">SALESFORCE</LdsBadge>
              ) : (
                <LdsBadge className="ml-4 grey-clr">MLR</LdsBadge>
              )}
              {isCurrent && item.expiration_date && (
                <span
                  className={`ml-4 expiration-tag ${getExpirationStatus(item.expiration_date)}`}
                  title={`Expiration Date: ${convertDateFormat(item.expiration_date)}`}
                >
                  {getExpirationStatus(item.expiration_date) === "expired"
                    ? "⏰ Expired:"
                    : "⏰ Expires:"}{" "}
                  {convertDateFormat(item.expiration_date)}
                </span>
              )}
            </div>
            <div className="row">
              <div className="col col-12 col-md item-title">
                <LdsLink href={`/details/${item.uuid}`}>
                  {item.list_title}
                </LdsLink>
              </div>
              <div className="col col-12 col-md del-link d-flex justify-end">
                {(canArchiveLists() || (((typeof item.created_by === 'object' ? item.created_by?.uuid : item.created_by) === userId) ||
                  (typeof item.created_by === 'object' && item.created_by?.email_id === userEmail))) &&
                status !== "APPROVED" && status !== "ARCHIVE" &&  (
                  <LdsIcon 
                    inline 
                    name="Info" 
                    onClick={() => {
                      setSelectedListItem(item);
                      setFullSelectedListItem(null);
                      setIsLoadingDetails(true);
                      setIsInfoModalOpen(true);
                      getSpecialityList(item.uuid).then((res) => {
                        if (res?.data?.data) {
                          setFullSelectedListItem(res.data.data);
                        }
                      }).catch(() => {}).finally(() => setIsLoadingDetails(false));
                    }}
                    style={{ fontSize: "1.5rem", marginRight: "2rem", cursor: "pointer" }} 
                    />
                )}
                {status !== "DRAFT" &&
                canArchiveLists() &&
                isCurrent ? (
                  <LdsButton
                    linkButton
                    onClick={() => setArchiveListID(item.uuid)}
                  >
                    Archive List
                  </LdsButton>
                ) : (
                  <></>
                )}
                {status === "DRAFT" &&
                canDeleteDrafts() ? (
                  <LdsButton
                    linkButton
                    onClick={() => deleteList(item.uuid)}
                    classes="ml-1"
                  >
                    Delete draft
                  </LdsButton>
                ) : (
                  <></>
                )}
                {status === "APPROVED" &&
                isSuperAdminRole() ? (
                  <LdsButton
                    linkButton
                    onClick={() => deleteList(item.uuid)}
                    classes="ml-4"
                  >
                    Delete Approved
                  </LdsButton>
                ) : (
                  <></>
                )}
                {!isCurrent &&
                canArchiveLists() &&
                isExpirationArchived(item) ? (
                  <LdsButton
                    linkButton
                    onClick={() => setUndoArchiveListID(item.uuid)}
                    classes="ml-4"
                  >
                    Undo Archive
                  </LdsButton>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });

  useEffect(() => {
    const recordsCopy = JSON.parse(JSON.stringify(visibleListRecords));

    // Check if any filters are active OR if admin has selected a user
    const hasActiveFilters = Object.keys(filters).some((filter) => filters[filter]);
    const hasUserFilter = selectedUserForFilter && selectedUserForFilter.trim() !== '';

    if (!hasActiveFilters && !hasUserFilter) {
      recordsCopy.forEach((record) => (record.show = true));
    } else {
      recordsCopy.forEach((record) => (record.show = false));

      // Build selected status set (OR within the status group)
      const selectedStatuses = [];
      if (filters.APPROVED) selectedStatuses.push("APPROVED");
      if (filters.DRAFT) selectedStatuses.push("DRAFT");
      if (filters["REQUEST FOR REVIEW"]) selectedStatuses.push("REQUEST FOR REVIEW");
      if (filters["REQUEST FOR APPROVAL"]) selectedStatuses.push("REQUEST FOR APPROVAL");

      // Build selected list-type set (OR within the type group)
      const selectedTypes = [];
      if (filters.SALESFORCE) selectedTypes.push("SALESFORCE");
      if (filters.MLR) selectedTypes.push("MLR");

      recordsCopy.forEach((record) => {
        let shouldShowRecord = true;
        const recordStatus = (record.list_status || "").toUpperCase();
        const recordType = record.list_quarter !== "" ? "SALESFORCE" : "MLR";

        // Type group: OR within type filters
        if (selectedTypes.length > 0) {
          shouldShowRecord = shouldShowRecord && selectedTypes.includes(recordType);
        }

        // Status group: OR within status filters
        if (selectedStatuses.length > 0) {
          shouldShowRecord = shouldShowRecord && selectedStatuses.includes(recordStatus);
        }

        // My Lists: AND with other groups
        if (filters["My Lists"]) {
          const createdByObj = typeof record.created_by === 'object' ? record.created_by : null;
          const recordCreatorName = createdByObj ? getCreatedBy(createdByObj) : '';
          shouldShowRecord = shouldShowRecord && !!recordCreatorName && recordCreatorName === userName;
        }
        // Admin user filter - check by email
        if (hasUserFilter) {
          const recordCreatorEmail = typeof record.created_by === 'object'
            ? record.created_by?.email_id
            : null;
          // Compare emails case-insensitively
          if (recordCreatorEmail && selectedUserForFilter) {
            shouldShowRecord = shouldShowRecord && recordCreatorEmail.toLowerCase() === selectedUserForFilter.toLowerCase();
          } else {
            shouldShowRecord = false;
          }
        }
        record.show = shouldShowRecord;
      });
    }
    setVisibleListRecords(recordsCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedUserForFilter]);

  useEffect(() => {
    let recordsCopy = JSON.parse(JSON.stringify(listRecords));
    recordsCopy = recordsCopy.map((record) => {
      return { ...record, show: true };
    });
    setVisibleListRecords(recordsCopy);
  }, [listRecords]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedUserForFilter]);

  const [commercialAll, medicalAll] = sortRecords();
  const filteredRecords = deptFilter === "COMMERCIAL"
    ? commercialAll
    : deptFilter === "MEDICAL"
      ? medicalAll
      : [...commercialAll, ...medicalAll];
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const pagedRecords = filteredRecords.slice(startIdx, startIdx + pageSize);
  const commercialPaged = pagedRecords.filter(r => r.department_field?.toUpperCase() === 'RETAIL');
  const medicalPaged = pagedRecords.filter(r => r.department_field?.toUpperCase() !== 'RETAIL');
  const recordsToDisplay = [commercialPaged, medicalPaged].filter(l => l.length > 0);

  return (
    <div>
      <div className="dept-toggle">
        <LdsBadge
          className={`dept-toggle-btn${deptFilter === "ALL" ? " active" : ""}`}
          onClick={() => { setDeptFilter("ALL"); setCurrentPage(1); }}
        >
          All
        </LdsBadge>
        <LdsBadge
          className={`dept-toggle-btn${deptFilter === "COMMERCIAL" ? " active" : ""}`}
          onClick={() => { setDeptFilter("COMMERCIAL"); setCurrentPage(1); }}
        >
          Commercial
        </LdsBadge>
        <LdsBadge
          className={`dept-toggle-btn${deptFilter === "MEDICAL" ? " active" : ""}`}
          onClick={() => { setDeptFilter("MEDICAL"); setCurrentPage(1); }}
        >
          Medical
        </LdsBadge>
      </div>
      {recordsToDisplay.length > 0 ? (
        recordsToDisplay.map((list, index) => (
          <Fragment key={index}>
            <div className="mt-4">
              <LdsBadge
                className="dept-badges"
                style={{ fontSize: "1.25rem", padding: "0.5rem 0.75rem" }}
              >
                { list[0].department_field && list[0].department_field.toUpperCase() === "RETAIL" ? "COMMERCIAL" : "MEDICAL"}
              </LdsBadge>
            </div>
            {renderList(list)}
          </Fragment>
        ))
      ) : (
        <p style={{ marginTop: "1.5rem" }}>No Record Found</p>
      )}

      {totalCount > 0 && (
        <div className="pagination-controls">
          <span className="pagination-info">
            Showing {startIdx + 1}–{Math.min(startIdx + pageSize, totalCount)} of {totalCount}
          </span>
          <div className="pagination-nav">
            <LdsButton
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safeCurrentPage === 1}
            >
              &laquo; Prev
            </LdsButton>
            <span className="pagination-page-info">Page {safeCurrentPage} of {totalPages}</span>
            <LdsButton
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage === totalPages}
            >
              Next &raquo;
            </LdsButton>
          </div>
          <div className="pagination-size">
            <label htmlFor={`page-size-${isCurrent ? 'current' : 'historical'}`}>Show:</label>
            <select
              id={`page-size-${isCurrent ? 'current' : 'historical'}`}
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      {isModalOpen && (
        <DeleteModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          showSuccessToast={showSuccessToast}
          resetFilters={resetFilters}
        />
      )}

      {isArchiveConfirmationModalOpen && (
        <ArchiveConfirmationModal
          open={isArchiveConfirmationModalOpen}
          setModalOpen={setIsArchiveConfirmationModalOpen}
          archiveList={archiveList}
        />
      )}
      {isUndoArchiveModalOpen && (
        <UndoArchiveConfirmationModal
          open={isUndoArchiveModalOpen}
          setModalOpen={setIsUndoArchiveModalOpen}
          undoArchive={undoArchive}
        />
      )}
      {isInfoModalOpen && selectedListItem && (
        
        <LdsModal
          modalId="list-status-info-modal"
          open={isInfoModalOpen}
          setModalOpen={setIsInfoModalOpen}
          title=""
          style={{ paddingTop: "4.9rem" }}
        >
           
         <div style={{ marginTop: "0.35rem" , minHeight:"200px"}}>
            <h3 style={{ marginBottom: "2.8rem" }}>
              <span><b>Current Status:</b></span> {(selectedListItem.list_status || "").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
            </h3>
            <StepTracker
              listType={selectedListItem.list_quarter !== "" ? "SALESFORCE" : "MLR"}
              currentStatus={selectedListItem.list_status}
              onRemind={handleRemind}
              isCooldownActive={isCooldownActive}
              specialtyList={fullSelectedListItem || selectedListItem}
              userTableList={userTableList}
              loadingLists={isLoadingDetails}
            />
          </div>
          

        </LdsModal>
       
      )}
      <LdsToast />
    </div>
  );
};

export default OverviewTile;
